import { useState, useRef } from 'react';

type SimpleType = 'text' | 'date' | 'number' | 'select' | 'autocomplete';
type MultiType = 'multiSelect' | 'multiAutocomplete';

export type FilterDef =
  | { type: SimpleType; param: string; transform?: (v: string) => any }
  | { type: MultiType; param: string }
  | { type: 'dateRange'; paramFrom: string; paramTo: string }
  | { type: 'numberRange'; paramMin: string; paramMax: string; transform?: (v: string) => any };

export type FilterSchema = Record<string, FilterDef>;

type ValueFor<D extends FilterDef> =
  D extends { type: SimpleType } ? string :
  D extends { type: MultiType } ? (string | number)[] :
  D extends { type: 'dateRange' } ? { from: string; to: string } :
  D extends { type: 'numberRange' } ? { min: string; max: string } :
  never;

export type FiltersFor<S extends FilterSchema> = { [K in keyof S]: ValueFor<S[K]> };

interface Options {
  onChange?: () => void;
}

const isFilterActive = (def: FilterDef, v: any): boolean => {
  switch (def.type) {
    case 'dateRange':
      return Boolean(v?.from || v?.to);
    case 'numberRange':
      return (v?.min !== '' && v?.min !== undefined) || (v?.max !== '' && v?.max !== undefined);
    case 'multiSelect':
    case 'multiAutocomplete':
      return Array.isArray(v) && v.length > 0;
    default:
      return v !== '' && v !== null && v !== undefined;
  }
};

export function useColumnFilters<S extends FilterSchema>(
  schema: S,
  options?: Options
) {
  const buildEmpty = (): FiltersFor<S> => {
    const out: any = {};
    for (const k in schema) {
      const def = schema[k];
      switch (def.type) {
        case 'dateRange':
          out[k] = { from: '', to: '' };
          break;
        case 'numberRange':
          out[k] = { min: '', max: '' };
          break;
        case 'multiSelect':
        case 'multiAutocomplete':
          out[k] = [];
          break;
        default:
          out[k] = '';
      }
    }
    return out;
  };

  const [filters, setFilters] = useState<FiltersFor<S>>(buildEmpty);
  const onChangeRef = useRef(options?.onChange);
  onChangeRef.current = options?.onChange;

  const update = <K extends keyof S>(key: K, value: FiltersFor<S>[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    onChangeRef.current?.();
  };

  const reset = () => {
    setFilters(buildEmpty());
    onChangeRef.current?.();
  };

  const hasActive = (): boolean => {
    for (const k in schema) {
      if (isFilterActive(schema[k], (filters as any)[k])) return true;
    }
    return false;
  };

  const toQueryParams = (): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const k in schema) {
      const def = schema[k];
      const v: any = (filters as any)[k];
      if (!isFilterActive(def, v)) continue;
      switch (def.type) {
        case 'dateRange':
          if (v.from) out[def.paramFrom] = v.from;
          if (v.to) out[def.paramTo] = v.to;
          break;
        case 'numberRange':
          if (v.min !== '' && v.min !== undefined) {
            out[def.paramMin] = def.transform ? def.transform(v.min) : Number(v.min);
          }
          if (v.max !== '' && v.max !== undefined) {
            out[def.paramMax] = def.transform ? def.transform(v.max) : Number(v.max);
          }
          break;
        case 'multiSelect':
        case 'multiAutocomplete':
          out[def.param] = v;
          break;
        default:
          out[def.param] = (def as { transform?: (v: string) => any }).transform
            ? (def as { transform: (v: string) => any }).transform(v)
            : v;
      }
    }
    return out;
  };

  return { filters, update, reset, hasActive, toQueryParams };
}
